import type { MailboxStatusItem } from '../api/mailboxes';

export type ToneClass =
    | 'neutral'
    | 'success'
    | 'warning'
    | 'danger'
    | 'muted';

/** Compact pill (Profile row + Connect Gmail modal) — matches existing badge styling */
export function mailboxStatusPillToneClasses(tone: ToneClass): string {
    switch (tone) {
        case 'success':
            return 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20';
        case 'warning':
            return 'bg-amber-400/10 text-amber-400 border-amber-400/20';
        case 'danger':
            return 'bg-red-400/10 text-red-400 border-red-400/20';
        case 'muted':
            return 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
        case 'neutral':
        default:
            return 'bg-sky-400/10 text-sky-300 border-sky-400/20';
    }
}

/** Inline alert surfaces (Profile / settings) */
export function recoverableGuidanceAlertClasses(tone: ToneClass): string {
    switch (tone) {
        case 'danger':
            return 'border-red-500/40 bg-red-500/10 text-red-100';
        case 'warning':
            return 'border-amber-500/40 bg-amber-500/10 text-amber-100';
        case 'muted':
            return 'border-zinc-500/40 bg-zinc-500/10 text-zinc-200';
        case 'success':
            return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100';
        case 'neutral':
        default:
            return 'border-sky-500/40 bg-sky-500/10 text-sky-100';
    }
}

export interface ChipDisplay {
    label: string;
    tone: ToneClass;
    /** Screen reader / tooltip text */
    ariaLabel: string;
}

/**
 * Connection dimension only — never conflate with sync health.
 */
export function getConnectionChipDisplay(connectionState: string): ChipDisplay {
    const s = (connectionState || '').toLowerCase();
    if (s === 'connected') {
        return { label: 'Connected', tone: 'success', ariaLabel: 'Gmail connection: connected' };
    }
    if (s === 'revoked') {
        return { label: 'Disconnected', tone: 'muted', ariaLabel: 'Gmail connection: disconnected' };
    }
    return {
        label: 'Auth error',
        tone: 'danger',
        ariaLabel: 'Gmail connection: authentication or authorization error',
    };
}

/** A completed sync older than this is shown as "Stale", not green "Synced". */
export const SYNC_STALE_AFTER_HOURS = 24;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const HAS_TIMEZONE_RE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;
const ISO_DATE_PREFIX_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** Reject impossible calendar dates (e.g. Feb 31) that JS Date normalizes silently. */
function matchesIsoCalendarComponents(iso: string, date: Date): boolean {
    const match = iso.match(ISO_DATE_PREFIX_RE);
    if (!match) return true;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return (
        date.getUTCFullYear() === year &&
        date.getUTCMonth() + 1 === month &&
        date.getUTCDate() === day
    );
}

/**
 * Backend sync timestamps represent UTC instants. Some legacy fields arrive as
 * naive ISO strings, which browsers otherwise parse as local time.
 */
export function parseMailboxStatusDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const raw = value.trim();
    if (!raw) return null;
    const iso = DATE_ONLY_RE.test(raw)
        ? `${raw}T00:00:00.000Z`
        : HAS_TIMEZONE_RE.test(raw)
            ? raw
            : `${raw}Z`;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return matchesIsoCalendarComponents(iso, d) ? d : null;
}

/** Age of the last successful sync in whole hours, or null when unknown/invalid. */
export function lastSyncAgeHours(
    lastSuccessfulSync: string | null | undefined,
    now: Date = new Date(),
): number | null {
    const d = parseMailboxStatusDate(lastSuccessfulSync);
    if (!d) return null;
    const ms = now.getTime() - d.getTime();
    if (ms < 0) return 0;
    return Math.floor(ms / (60 * 60 * 1000));
}

export function formatSyncAge(ageHours: number): string {
    if (ageHours < 24) return `${ageHours}h ago`;
    const days = Math.floor(ageHours / 24);
    return days === 1 ? '1 day ago' : `${days} days ago`;
}

/**
 * Sync health + in-progress activity (canonical API fields).
 *
 * When `lastSuccessfulSync` is provided and the last completed sync is older
 * than SYNC_STALE_AFTER_HOURS, "Synced" is downgraded to an amber "Stale"
 * chip — a 10-day-old sync must never render the same as a fresh one.
 */
export function getSyncHealthChipDisplay(
    syncHealth: string,
    syncInProgress: boolean,
    lastSuccessfulSync?: string | null,
    now: Date = new Date(),
): ChipDisplay {
    if (syncInProgress) {
        return {
            label: 'Syncing',
            tone: 'warning',
            ariaLabel: 'Mailbox sync is in progress',
        };
    }
    const h = (syncHealth || 'idle').toLowerCase();
    if (h === 'failed') {
        return {
            label: 'Sync failed',
            tone: 'danger',
            ariaLabel: 'Last sync failed',
        };
    }
    if (h === 'syncing') {
        return {
            label: 'Syncing',
            tone: 'warning',
            ariaLabel: 'Mailbox sync active',
        };
    }
    if (h === 'completed') {
        const ageHours = lastSyncAgeHours(lastSuccessfulSync, now);
        if (ageHours !== null && ageHours >= SYNC_STALE_AFTER_HOURS) {
            const age = formatSyncAge(ageHours);
            return {
                label: `Stale (${age})`,
                tone: 'warning',
                ariaLabel: `Last successful sync was ${age} — data may be out of date`,
            };
        }
        return {
            label: 'Synced',
            tone: 'success',
            ariaLabel: 'Last sync completed successfully',
        };
    }
    return {
        label: 'Idle',
        tone: 'neutral',
        ariaLabel: 'Sync idle — no run in progress',
    };
}

export function formatLastSuccessfulSync(iso: string | null | undefined, locale?: string): string {
    if (!iso) return 'No successful sync yet';
    try {
        const d = parseMailboxStatusDate(iso);
        if (!d) return 'No successful sync yet';
        return d.toLocaleString(locale);
    } catch {
        return 'No successful sync yet';
    }
}

export interface RecoverableGuidance {
    headline: string;
    nextStep: string;
    tone: ToneClass;
}

/**
 * Plain-language next step from server error_class (not keyword-matching sync_error).
 */
export function getRecoverableGuidance(errorClass: string | null | undefined): RecoverableGuidance | null {
    if (!errorClass) return null;
    switch (errorClass) {
        case 'oauth_expired':
            return {
                headline: 'Gmail authorization expired',
                nextStep: 'Reconnect this mailbox to restore access.',
                tone: 'warning',
            };
        case 'sync_failed':
            return {
                headline: 'Sync failed',
                nextStep: 'Try syncing again. If it keeps failing, check your network or reconnect Gmail.',
                tone: 'danger',
            };
        case 'connection_error':
            return {
                headline: 'Connection problem',
                nextStep: 'Reconnect Gmail — we could not use the saved credentials for this mailbox.',
                tone: 'danger',
            };
        case 'connection_revoked':
            return {
                headline: 'Mailbox disconnected',
                nextStep: 'Connect this mailbox again to resume syncing.',
                tone: 'muted',
            };
        case 'provider_sync_unavailable':
            return {
                headline: 'Inbox sync not available yet',
                nextStep: 'This mailbox is connected through a provider adapter. Email sync will be enabled in a future release.',
                tone: 'muted',
            };
        default:
            return null;
    }
}

/** No completed auto-sync run within this window ⇒ auto-sync is considered not working. */
export const AUTO_SYNC_UNHEALTHY_AFTER_HOURS = 2;

/**
 * Warning copy when the auto-sync toggle is on but the server is not actually
 * running hourly syncs. Returns null when healthy or not applicable.
 *
 * `schedulerEnabled` reflects the API process; a separate worker may still sync,
 * which is why a recent `lastAutoSyncAt` also counts as healthy.
 */
export function getAutoSyncHealthWarning(params: {
    autoSyncEnabled: boolean;
    hasConnectedMailbox: boolean;
    schedulerEnabled: boolean | null | undefined;
    lastAutoSyncAt: string | null | undefined;
    now?: Date;
}): string | null {
    const { autoSyncEnabled, hasConnectedMailbox, schedulerEnabled, lastAutoSyncAt } = params;
    if (!autoSyncEnabled || !hasConnectedMailbox) return null;
    if (schedulerEnabled) return null;
    const now = params.now ?? new Date();
    const ageHours = lastSyncAgeHours(lastAutoSyncAt, now);
    if (ageHours !== null && ageHours < AUTO_SYNC_UNHEALTHY_AFTER_HOURS) return null;
    if (ageHours === null) {
        return 'Auto-sync is enabled, but no automatic sync has ever run. The server sync scheduler appears to be off — emails will only update when you sync manually.';
    }
    return `Auto-sync is enabled, but the last automatic sync was ${formatSyncAge(ageHours)}. The server sync scheduler appears to be off — emails will only update when you sync manually.`;
}

export function connectionDotClass(tone: ToneClass): string {
    if (tone === 'success') return 'bg-emerald-400';
    if (tone === 'danger') return 'bg-red-400';
    return 'bg-zinc-400';
}

export function syncDotClass(tone: ToneClass): string {
    if (tone === 'success') return 'bg-emerald-400';
    if (tone === 'warning') return 'bg-amber-400 animate-pulse';
    if (tone === 'danger') return 'bg-red-400';
    return 'bg-sky-400';
}

/** Optional detail line — never the primary source of truth for classification */
export function getSyncErrorDetail(syncError: string | null | undefined): string | null {
    const t = (syncError || '').trim();
    return t.length > 0 ? t : null;
}

export interface MailboxCompositeSummary {
    connection: ChipDisplay;
    syncHealth: ChipDisplay;
    lastSuccessfulSyncLabel: string;
    guidance: RecoverableGuidance | null;
    syncErrorDetail: string | null;
}

export function buildMailboxCompositeSummary(mailbox: MailboxStatusItem): MailboxCompositeSummary {
    const connection = getConnectionChipDisplay(mailbox.connection_state);
    const syncHealth = getSyncHealthChipDisplay(
        mailbox.sync_health,
        mailbox.sync_in_progress,
        mailbox.last_successful_sync ?? mailbox.last_synced_at,
    );
    const lastSuccessfulSyncLabel = formatLastSuccessfulSync(mailbox.last_successful_sync ?? mailbox.last_synced_at);
    const guidance = getRecoverableGuidance(mailbox.error_class);
    const syncErrorDetail = getSyncErrorDetail(mailbox.sync_error);
    return {
        connection,
        syncHealth,
        lastSuccessfulSyncLabel,
        guidance,
        syncErrorDetail,
    };
}

/**
 * Compact strings for unit tests (matrix: dimensions → labels + next step).
 */
export function summarizeMailboxForTests(mailbox: MailboxStatusItem): {
    connectionLabel: string;
    syncLabel: string;
    nextStepHint: string;
} {
    const c = buildMailboxCompositeSummary(mailbox);
    return {
        connectionLabel: c.connection.label,
        syncLabel: c.syncHealth.label,
        nextStepHint: c.guidance?.nextStep ?? '',
    };
}
